<template>
  <div class="chatbot-header p-4 bg-gradient-to-r from-blue-800 to-blue-700 border-b border-blue-900 shadow-md">
    <div class="flex flex-col">
      <div class="flex items-center justify-between">
        <div class="flex items-center">
          <h3 class="text-xl font-bold text-white mr-2">{{ chatbotName }}</h3>
          <div class="text-xs text-blue-100">
            <span class="font-medium text-white/90">{{ userName || 'User' }}</span>
            <span class="mx-1 text-blue-300/80">•</span>
            <span class="text-white/80">{{ formatBusinessRole(businessRole) }}</span>
          </div>
        </div>
        <div class="flex items-center space-x-2">
          <button 
            @click="$emit('toggle-history')" 
            class="p-2 bg-white/20 backdrop-blur-sm rounded-full text-white hover:bg-white/30 transition-colors border border-white/30 flex items-center justify-center"
            aria-label="Chat history"
            title="View chat history"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
          </button>
          <div class="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-white text-xs font-medium border border-white/30">
            {{ formatChatbotEnv(environment) }}
          </div>
        </div>
      </div>
      <p class="text-sm text-blue-100 mt-1">{{ chatbotDescription }}</p>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, computed } from 'vue';
import { BusinessRole, formatBusinessRole } from '@/types/businessRole';
import { ChatbotEnv, formatChatbotEnv } from '@/types/chatbotEnv';

export default defineComponent({
  name: 'ChatbotHeader',
  props: {
    businessRole: {
      type: String as PropType<string>,
      default: BusinessRole.PLANNER
    },
    environment: {
      type: String as PropType<ChatbotEnv>,
      default: ChatbotEnv.GLOBAL
    },
    userName: {
      type: String,
      default: 'User'
    },
    userAvatar: {
      type: String,
      default: null
    }
  },
  emits: ['toggle-history'],
  setup(props) {
    // Compute the chatbot name based on business role and environment
    const chatbotName = computed(() => {
      const env = props.environment || ChatbotEnv.GLOBAL;
      
      // Different names based on environment
      switch (env) {
        case ChatbotEnv.GLOBAL:
          return "Fraport Assistant";
        case ChatbotEnv.FORECASTING_CALENDAR:
          return "Calendar Assistant";
        case ChatbotEnv.PLANNER_FILE_HANDLING:
          return "Forecasting Co\u2011Pilot";
        case ChatbotEnv.FORECASTING_KANBAN:
          return "Kanban Assistant";
        default:
          return "Fraport Assistant";
      }
    });
    
    // Compute the chatbot description based on business role and environment
    const chatbotDescription = computed(() => {
      const businessRole = props.businessRole || BusinessRole.PLANNER;
      const env = props.environment || ChatbotEnv.GLOBAL;
      
      // Different descriptions based on environment and role
      if (env === ChatbotEnv.GLOBAL) {
        if (businessRole === BusinessRole.HEAD_OF_PCO) {
          return "Your personal assistant for airport management and forecasting oversight.";
        } else if (businessRole === BusinessRole.CONTROLLER) {
          return "Your assistant for financial analysis and forecasting control.";
        } else {
          return "Your personal assistant for airport operations and planning. Limited access for financial data because of Planer role.";
        }
      } else if (env === ChatbotEnv.FORECASTING_CALENDAR) {
        return "I can help you manage your forecasting schedule and deadlines.";
      } else if (env === ChatbotEnv.PLANNER_FILE_HANDLING) {
        return "Supporting your forecasting workflow: templates, uploads, reports, and data insights.";
      } else if (env === ChatbotEnv.FORECASTING_KANBAN) {
        return "I can help you track and manage your forecasting tasks.";
      } else {
        return "How can I assist you today?";
      }
    });
    
    return {
      chatbotName,
      chatbotDescription,
      formatBusinessRole,
      formatChatbotEnv
    };
  }
});
</script>

<style scoped>
.chatbot-header {
  position: relative;
  overflow: hidden;
}

.chatbot-header::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 60%);
  opacity: 0.5;
  pointer-events: none;
}

.chatbot-header h3 {
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  letter-spacing: 0.01em;
}

/* Add subtle animation to the environment badge */
@keyframes pulse {
  0% { opacity: 0.9; }
  50% { opacity: 1; }
  100% { opacity: 0.9; }
}

.chatbot-header div[class*="bg-white"] {
  animation: pulse 3s infinite ease-in-out;
}
</style>